// Utilities for "couple patients" — a single patient document representing a
// couple (two partners). The patient doc keeps the legacy fields
// (firstName/lastName/phone/idNumber) denormalized from the partner data so
// every existing consumer (display, search, SMS, invoices, activity logs)
// keeps working unchanged. Pure JS — no Firebase imports.

export const emptyPartner = () => ({
  firstName: "",
  lastName: "",
  phone: "",
  idNumber: "",
});

const clean = (val) => (typeof val === "string" ? val.trim() : "");

const isPartnerEmpty = (partner) =>
  !clean(partner?.firstName) && !clean(partner?.lastName);

/**
 * Build the combined Hebrew display name for a couple.
 * Same last name:      { firstName: "יוסי ורונית", lastName: "כהן" }   → "יוסי ורונית כהן"
 * Different last name: { firstName: "יוסי כהן ורונית", lastName: "לוי" } → "יוסי כהן ורונית לוי"
 * Degrades gracefully when one partner is empty.
 */
export const combineCoupleNames = (partner1, partner2) => {
  const p1First = clean(partner1?.firstName);
  const p1Last = clean(partner1?.lastName);
  const p2First = clean(partner2?.firstName);
  const p2Last = clean(partner2?.lastName);

  if (isPartnerEmpty(partner2)) {
    return { firstName: p1First, lastName: p1Last };
  }
  if (isPartnerEmpty(partner1)) {
    return { firstName: p2First, lastName: p2Last };
  }
  if (!p1First || !p2First) {
    // One partner has only a last name — fall back to whichever side is fuller.
    const primary = p1First ? { firstName: p1First, lastName: p1Last } : { firstName: p2First, lastName: p2Last };
    return primary;
  }

  if (p1Last === p2Last) {
    return { firstName: `${p1First} ו${p2First}`, lastName: p1Last };
  }
  return {
    firstName: `${p1First}${p1Last ? ` ${p1Last}` : ""} ו${p2First}`,
    lastName: p2Last,
  };
};

/**
 * Compute the legacy denormalized fields for a couple patient.
 * phone/idNumber come from the partner marked as SMS-primary.
 */
export const buildCoupleDenormalizedFields = ({
  partner1,
  partner2,
  smsPrimaryPartner,
}) => {
  const primary = smsPrimaryPartner === 2 ? partner2 : partner1;
  const { firstName, lastName } = combineCoupleNames(partner1, partner2);
  return {
    firstName,
    lastName,
    phone: clean(primary?.phone),
    idNumber: clean(primary?.idNumber) || null,
  };
};

/**
 * Returns [partner1, partner2] when the patient is a couple, else null.
 */
export const getCouplePartners = (patient) => {
  if (patient?.isCouple !== true) return null;
  return [
    { ...emptyPartner(), ...(patient.partner1 || {}) },
    { ...emptyPartner(), ...(patient.partner2 || {}) },
  ];
};
